import { Column } from "@/components/Column";

import { IS_PLAYYARD_HALLOWEEN } from "@/lib/constants";
import { playyardSky } from "@/lib/playyardSky";
import { PlayyardSky } from "@/types";

const STAR_COUNT = 50;
// Halloween swaps in a big orange harvest moon.
const MOON = IS_PLAYYARD_HALLOWEEN
  ? { color: "#f6a54d", glow: "0.5", sizeCqw: 8 }
  : { color: "#f5f0cf", glow: "0.3", sizeCqw: 5 };
const CLOUD_SHAPES = [
  { top: 8, width: 14, durationS: 70 },
  { top: 14, width: 10, durationS: 95 },
  { top: 23, width: 18, durationS: 120 },
  { top: 20, width: 8, durationS: 80 },
  { top: 4, width: 12, durationS: 105 },
  { top: 28, width: 16, durationS: 130 },
  { top: 11, width: 11, durationS: 90 },
];
// Wind hurries the clouds along.
const WINDY_CLOUD_SPEEDUP = 3;

const STARS = Array.from({ length: STAR_COUNT }, (_, index) => ({
  left: playyardSky.random(index + 1) * 100,
  top: playyardSky.random(index + 101) * 55,
  delayS: -playyardSky.random(index + 201) * 3,
}));

export const PlayyardSkyLayer = ({ sky }: { sky: PlayyardSky }) => {
  return (
    <Column className="pointer-events-none absolute inset-0 overflow-hidden">
      <Column
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(var(--yard-skyTop), var(--yard-skyMid) 40%, var(--yard-skyBottom) 58%)",
        }}
      />
      <Column
        className="absolute inset-0 transition-opacity duration-1000"
        style={{ opacity: sky.starOpacity }}
      >
        {STARS.map((star, index) => (
          <Column
            key={index}
            className="staffer-pet-yard-twinkle absolute h-[2px] w-[2px] rounded-full bg-white"
            style={{
              left: `${star.left.toString()}%`,
              top: `${star.top.toString()}%`,
              animationDelay: `${star.delayS.toString()}s`,
            }}
          />
        ))}
      </Column>
      <PlayyardSkyBody
        color="var(--yard-sun)"
        glow="var(--yard-glow)"
        position={sky.sun}
        sizeCqw={9}
      />
      <PlayyardSkyBody
        color={MOON.color}
        glow={MOON.glow}
        position={sky.moon}
        sizeCqw={MOON.sizeCqw}
      />
      {CLOUD_SHAPES.slice(0, sky.cloudCount).map((cloud, index) => {
        const durationS = sky.isWindy
          ? cloud.durationS / WINDY_CLOUD_SPEEDUP
          : cloud.durationS;
        return (
          <Column
            key={index}
            className="staffer-pet-yard-drift absolute left-0 h-[3cqw] rounded-full opacity-90 before:absolute before:bottom-[20%] before:left-[14%] before:h-[170%] before:w-[45%] before:rounded-full before:bg-inherit after:absolute after:bottom-[25%] after:left-[48%] after:h-[140%] after:w-[35%] after:rounded-full after:bg-inherit"
            style={{
              top: `${cloud.top.toString()}%`,
              width: `${cloud.width.toString()}cqw`,
              backgroundColor: "var(--yard-cloud)",
              animationDuration: `${durationS.toString()}s`,
              animationDelay: `${(-durationS * playyardSky.random(index + 7)).toString()}s`,
            }}
          />
        );
      })}
    </Column>
  );
};

const PlayyardSkyBody = ({
  color,
  glow,
  position,
  sizeCqw,
}: {
  color: string;
  glow: string;
  position: { x: number; y: number; opacity: number };
  sizeCqw: number;
}) => {
  return (
    <Column
      className="absolute rounded-full transition-[left,top,opacity] duration-1000"
      style={{
        left: `${position.x.toString()}%`,
        top: `${position.y.toString()}%`,
        width: `${sizeCqw.toString()}cqw`,
        height: `${sizeCqw.toString()}cqw`,
        opacity: position.opacity,
        transform: "translate(-50%, -50%)",
        background: color,
        boxShadow: `0 0 3cqw 1.5cqw color-mix(in srgb, ${color} 70%, transparent), 0 0 12cqw 6cqw color-mix(in srgb, ${color} calc(${glow} * 100%), transparent)`,
      }}
    />
  );
};
